/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Modal } from '@az/starc-ui';
import { type ExtendedProductReviewImage } from '../../hooks/useProductReviewsImages';
import { GalleryView } from './components/GalleryView/GalleryView';
import { DetailsView } from './components/DetailsView/DetailsView';
import { type ProductReview } from '../../interface';
import { type ActiveImage } from '../../hooks/useReviewImageModal';
import styles from './styles.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';

export type CustomerImagesModalProps = {
  isOpen: boolean;
  hasBackButton?: boolean;
  onClose: () => void;
  onImageChange: (image: ActiveImage, hasBackButton?: boolean) => void;
  images: ExtendedProductReviewImage[];
  image: ActiveImage;
  review: ProductReview | undefined;
};

const labelMap = {
  back: 'label_back',
};

export const CustomerImagesModal = ({
  isOpen,
  hasBackButton,
  images,
  review,
  image,
  onClose,
  onImageChange,
}: CustomerImagesModalProps) => {
  const labels = useLabels(labelMap);
  const isDetailsView = !!image;

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const deselectImage = () => {
    onImageChange(null);
  };

  const onImageClick = (image: ActiveImage) => {
    const withBackButton = true;
    onImageChange(image, withBackButton);
  };

  const changeImage = (image: ActiveImage) => {
    onImageChange(image, hasBackButton);
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeByClickAway
      className={styles.root}
      hasCloseButton
      size={{ s: 'full', m: 'full', l: 'large', xl: 'large' }}
      backdropClassName={styles.backdrop}
      headerColor={isDesktop ? 'primary' : 'secondary'}
      hasHeaderBackButton={hasBackButton}
      backButtonTitle={labels.back}
      onBackClick={deselectImage}
    >
      {isDetailsView ? (
        <DetailsView image={image} onImageChange={changeImage} images={images} review={review} />
      ) : (
        <GalleryView images={images} onImageClick={onImageClick} />
      )}
    </Modal>
  );
};
